import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';



const CommonConcerns = () => {
  const [activeAccordion, setActiveAccordion] = useState(0);
    const navigate = useNavigate();

  const concerns = [
    {
      question: "Will our business operations be interrupted?",
      answer: `Our phased installation approach ensures minimal disruption to your daily operations. We work during off-hours when possible and maintain parallel systems during the transition.\n\nTypical disruption is limited to 2-4 hours during the final cutover, which we schedule at your convenience. Most clients experience zero downtime during 90% of the installation process.`,
      icon: "AlertCircle",
      caseStudy: "TechCorp Manufacturing completed their conversion with only 3 hours of planned downtime over a weekend."
    },
    {
      question: "How long does the conversion process take?",
      answer: `Complete conversion typically takes 4-10 weeks depending on building size and complexity. Here's the typical timeline:\n\n• Assessment: 1-2 weeks\n• Design & Planning: 1-2 weeks\n• Installation: 2-6 weeks\n• Testing & Go-live: 1 week\n\nWe provide detailed project timelines with milestone tracking throughout the process.`,
      icon: "Clock",
      caseStudy: "Regional Law Firm (3 floors, 75 employees) completed conversion in 6 weeks with zero business impact."
    },
    {
      question: "What about our existing equipment and investments?",
      answer: `We maximize your existing infrastructure investments wherever possible. Many network switches, routers, and endpoints are fiber-compatible.\n\nOur assessment identifies which equipment can be retained and what needs upgrading. We often help clients repurpose copper infrastructure for backup systems or secondary networks.`,
      icon: "HardDrive",
      caseStudy: "Healthcare Partners saved $12,000 by reusing 80% of their existing network equipment."
    },
    {
      question: "Is fiber optic technology reliable for mission-critical operations?",
      answer: `Fiber optic networks are significantly more reliable than copper systems. They're immune to electromagnetic interference, weather-resistant, and have much lower failure rates.\n\nWe design redundant pathways and backup systems for mission-critical applications. Our monitoring systems detect and resolve issues before they impact operations.`,
      icon: "Shield",
      caseStudy: "Financial Services Corp achieved 99.98% uptime in their first year after conversion."
    },
    {
      question: "How do we justify the investment to stakeholders?",
      answer: `The ROI case is compelling with typical payback periods of 12-18 months. Key benefits include:\n\n• 25-50% productivity improvements\n• 75% reduction in maintenance costs\n• 80% fewer network-related issues\n• Future-proofing for 20+ years\n\nWe provide detailed ROI analysis and business case documentation for your stakeholders.`,
      icon: "TrendingUp",
      caseStudy: "Consulting Group saw 340% ROI in year one through improved client service capabilities."
    },
    {
      question: "What ongoing support and maintenance is required?",
      answer: `Fiber networks require minimal maintenance compared to copper systems. Our support includes:\n\n• 24/7 network monitoring\n• Proactive maintenance scheduling\n• Emergency response within 2 hours\n• Regular performance optimization\n• Technology upgrade planning\n\nMost clients see 75% reduction in IT support tickets after conversion.`,
      icon: "Headphones",
      caseStudy: "Property Management Company reduced IT support costs by $18,000 annually."
    }
  ];

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? -1 : index);
  };

  return (
    <div className="bg-surface rounded-xl p-8 mb-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Common Concerns Addressed
        </h2>
        <p className="text-text-secondary text-lg max-w-3xl mx-auto">
          We understand the challenges of infrastructure transformation. 
          Here are answers to the most common questions from business leaders.
        </p>
      </div>
      <div className="max-w-4xl mx-auto space-y-4">
        {concerns?.map((concern, index) => (
          <div key={index} className="bg-white rounded-lg shadow-brand-subtle overflow-hidden">
            <button
              onClick={() => toggleAccordion(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-lg mr-4">
                  <Icon name={concern?.icon} size={20} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary">
                  {concern?.question}
                </h3>
              </div>
              <Icon 
                name={activeAccordion === index ? "ChevronUp" : "ChevronDown"} 
                size={20} 
                className="text-text-secondary transition-transform duration-200" 
              />
            </button>
            
            <div className={`expandable-content ${activeAccordion === index ? 'expanded' : ''}`}>
              <div className="px-6 pb-6">
                <div className="pl-16">
                  <div className="prose prose-gray max-w-none">
                    {concern?.answer?.split('\n')?.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-text-secondary mb-3 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                    <div className="flex items-start">
                      <Icon name="CheckCircle" size={16} className="mr-3 mt-1 text-accent flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-accent">Success Story: </span>
                        <span className="text-text-primary">{concern?.caseStudy}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-12">
        <div className="bg-white rounded-lg p-6 shadow-brand-moderate max-w-2xl mx-auto">
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Still have questions?
          </h3>
          <p className="text-text-secondary mb-6">
            Our fiber optic specialists are ready to address your specific concerns 
            and provide detailed answers for your unique situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <Button
              variant="outline"
              iconName="Phone"
              iconPosition="left"
            >
              Call Expert: 01483 343 064
            </Button> */}
            <Button
              variant="default"
              iconName="MessageSquare"
              iconPosition="left"
              className="cta-primary"
              onClick={() => navigate('/get-quote-consultation')}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonConcerns;